/**
 * =============================================================================
 * SurfaceSpriteManager - Manager de sprites de superficies desde atlas
 * =============================================================================
 * 
 * Maneja la precarga y creación de texturas para superficies indexadas desde
 * archivos atlas. Cada superficie es un tile extraído de un PNG más grande.
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '@shared/constants';
import type { SurfaceDefinition, SurfaceIndex, AssetsIndex } from '@shared/types/surface';
import { getAtlasTextureKey, getAtlasTilePosition, getSurfaceSpriteKey } from '@shared/types/surface';

// Índice de superficies cargado
let surfaceIndex: SurfaceIndex = {};

// Verbosidad de los logs de carga de atlas/superficies. Ponerlo en `true` para debuggear qué atlas
// se cargan y cuántas superficies se configuran. Por defecto silencioso (spammeaba la consola en
// cada boot / cada fondo de auth). Los `console.warn` de error NO se gatean con esto.
const LOG_SURFACE_LOADING = false;

// NOTA: la idempotencia (atlas ya cargado / superficie ya horneada) se chequea
// SIEMPRE contra `scene.textures.exists(...)`, que es POR INSTANCIA de Phaser.
// No usar Sets a nivel de módulo: el login (MapBackgroundRenderer), el juego
// (GameScene) y los editores son Phaser.Game distintos con texture managers
// distintos. Un Set global hacía que el login "marcara" las superficies y el
// juego las salteara → pasto/costas invisibles in-game.

/**
 * Obtiene el índice de superficies actual
 */
export function getSurfaceIndex(): SurfaceIndex {
  return surfaceIndex;
}

/**
 * Obtiene una superficie por su ID
 */
export function getSurfaceById(surfaceId: string): SurfaceDefinition | null {
  return surfaceIndex[surfaceId] || null;
}

/**
 * Obtiene la clave de textura para una superficie
 * Puede ser el ID directo o una clave generada desde el atlas
 */
export function getSurfaceTextureKey(surfaceId: string): string {
  return getSurfaceSpriteKey(surfaceId);
}

/**
 * Verifica si una superficie está disponible como textura
 */
export function isSurfaceTextureAvailable(scene: Phaser.Scene, surfaceId: string): boolean {
  const textureKey = getSurfaceTextureKey(surfaceId);
  return scene.textures.exists(textureKey);
}

/**
 * Precarga los atlas necesarios para las superficies.
 * Se debe llamar en el preload() de la escena.
 * 
 * @param scene La escena de Phaser
 * @param assetsIndex El índice de assets (puede contener surfaces)
 */
/**
 * Resuelve la URL de un atlas de superficie:
 * - Atlas de dungeon (`dungeons/<nombre>/<archivo>.png`) → `assets/world/dungeons/…`
 *   (se movieron de `world/floors/dungeons/` a `world/dungeons/`).
 * - Floors del mundo (`pattern1..8.png`, `floors1..3.png`) → `assets/world/floors/…`.
 * - Cualquier otro atlas → `assets/pattern/…`.
 */
export function surfaceAtlasUrl(atlasFile: string): string {
  if (atlasFile.startsWith('dungeons/')) {
    return `/assets/world/${atlasFile}`;
  }
  if (/^(pattern[1-8]|floors[1-3])\.png$/i.test(atlasFile)) {
    return `/assets/world/floors/${atlasFile}`;
  }
  return `/assets/pattern/${atlasFile}`;
}

export function preloadSurfaceAtlases(
  scene: Phaser.Scene,
  assetsIndex: AssetsIndex
): void {
  if (!assetsIndex.surfaces) {
    if (LOG_SURFACE_LOADING) console.log('[SurfaceSpriteManager] No hay superficies en assets-index');
    return;
  }
  
  surfaceIndex = assetsIndex.surfaces;
  
  // Obtener lista de atlas únicos. Las superficies de AGUA se saltean: el agua NO se
  // renderiza desde un atlas de superficie sino con frames animados (WaterSpriteManager,
  // ver MapBackgroundRenderer/GameScene). La entrada vestigial `water_1` (atlasFile
  // `water.png`, que ni existe) hacía fallar la carga con "atlas_water" en cada fondo.
  const atlasFiles = new Set<string>();
  for (const surface of Object.values(surfaceIndex)) {
    if (surface.type === 'water') continue;
    if (surface.atlasFile) {
      atlasFiles.add(surface.atlasFile);
    }
  }
  
  if (LOG_SURFACE_LOADING) console.log(`[SurfaceSpriteManager] Precargando ${atlasFiles.size} atlas con ${Object.keys(surfaceIndex).length} superficies`);
  
  // Precargar cada atlas (idempotencia por instancia: si ESTA escena ya lo tiene).
  for (const atlasFile of atlasFiles) {
    const textureKey = getAtlasTextureKey(atlasFile);

    if (!scene.textures.exists(textureKey)) {
      if (LOG_SURFACE_LOADING) console.log(`[SurfaceSpriteManager] Cargando atlas: ${atlasFile} como ${textureKey}`);
      // pattern1..8 son los floors del mundo (assets/world/floors/); el resto sigue en assets/pattern/.
      scene.load.image(textureKey, surfaceAtlasUrl(atlasFile));
    }
  }
}

/**
 * Configura las texturas individuales para cada superficie.
 * Se debe llamar en el create() de la escena, después de que los atlas se hayan cargado.
 * 
 * @param scene La escena de Phaser
 */
export function setupSurfaceTextures(scene: Phaser.Scene): void {
  if (Object.keys(surfaceIndex).length === 0) {
    if (LOG_SURFACE_LOADING) console.log('[SurfaceSpriteManager] No hay superficies para configurar');
    return;
  }
  
  let configured = 0;
  
  for (const [surfaceId, surface] of Object.entries(surfaceIndex)) {
    // El agua se renderiza animada (WaterSpriteManager), no desde un atlas de superficie.
    if (surface.type === 'water') {
      continue;
    }

    const surfaceKey = getSurfaceTextureKey(surfaceId);

    // Idempotencia POR INSTANCIA: si ESTA escena ya tiene la textura, saltar.
    if (scene.textures.exists(surfaceKey)) {
      continue;
    }

    const atlasKey = getAtlasTextureKey(surface.atlasFile);

    // Verificar que el atlas está cargado
    if (!scene.textures.exists(atlasKey)) {
      console.warn(`[SurfaceSpriteManager] Atlas ${surface.atlasFile} no está cargado para superficie ${surfaceId}`);
      continue;
    }

    // Calcular la posición en el atlas
    const { x, y } = getAtlasTilePosition(surface.gridX, surface.gridY, TILE_SIZE);
    
    // Calcular el tamaño (puede ocupar múltiples tiles)
    const tilesX = surface.tilesX || 1;
    const tilesY = surface.tilesY || 1;
    const width = tilesX * TILE_SIZE;
    const height = tilesY * TILE_SIZE;
    
    // Obtener la imagen del atlas
    const atlasTexture = scene.textures.get(atlasKey);
    const sourceImage = atlasTexture.getSourceImage() as HTMLImageElement;
    
    // Crear un canvas para extraer el tile (o área de tiles)
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.error(`[SurfaceSpriteManager] No se pudo crear contexto 2D para ${surfaceId}`);
      continue;
    }
    
    // Dibujar el área del atlas en el canvas
    ctx.drawImage(
      sourceImage,
      x, y, width, height,  // Origen en el atlas
      0, 0, width, height   // Destino en el canvas
    );
    
    // Añadir el canvas como una nueva textura
    scene.textures.addCanvas(surfaceKey, canvas);

    configured++;
  }
  
  if (configured > 0 && LOG_SURFACE_LOADING) {
    console.log(`[SurfaceSpriteManager] ${configured} superficies configuradas como texturas`);
  }
}

/**
 * Crea un sprite de superficie en la escena.
 * 
 * @param scene La escena de Phaser
 * @param surfaceId ID de la superficie
 * @param x Posición X en el mundo
 * @param y Posición Y en el mundo
 * @returns El sprite creado, o null si la superficie no existe
 */
export function createSurfaceSprite(
  scene: Phaser.Scene,
  surfaceId: string,
  x: number,
  y: number
): Phaser.GameObjects.Image | null {
  const surface = getSurfaceById(surfaceId);
  if (!surface) {
    console.warn(`[SurfaceSpriteManager] Superficie no encontrada: ${surfaceId}`);
    return null;
  }
  
  const textureKey = getSurfaceTextureKey(surfaceId);
  
  if (!scene.textures.exists(textureKey)) {
    console.warn(`[SurfaceSpriteManager] Textura no disponible para: ${surfaceId}`);
    return null;
  }
  
  return scene.add.image(x, y, textureKey);
}

/**
 * Obtiene todas las superficies agrupadas por tipo.
 * Útil para el selector del Map Editor.
 */
export function getSurfacesByType(): Record<string, Array<{ id: string; surface: SurfaceDefinition }>> {
  const grouped: Record<string, Array<{ id: string; surface: SurfaceDefinition }>> = {};
  
  for (const [id, surface] of Object.entries(surfaceIndex)) {
    if (!grouped[surface.type]) {
      grouped[surface.type] = [];
    }
    grouped[surface.type].push({ id, surface });
  }
  
  return grouped;
}

/**
 * Obtiene todas las superficies agrupadas por tipo y luego por subtipo.
 * Útil para el selector del Map Editor con mejor organización.
 * Retorna: Record<type, Record<subtype, Array<{ id, surface }>>>
 */
export function getSurfacesByTypeAndSubtype(): Record<string, Record<string, Array<{ id: string; surface: SurfaceDefinition }>>> {
  const grouped: Record<string, Record<string, Array<{ id: string; surface: SurfaceDefinition }>>> = {};
  
  for (const [id, surface] of Object.entries(surfaceIndex)) {
    const type = surface.type || 'misc';
    const subtype = surface.subtype || 'default';
    
    if (!grouped[type]) {
      grouped[type] = {};
    }
    if (!grouped[type][subtype]) {
      grouped[type][subtype] = [];
    }
    grouped[type][subtype].push({ id, surface });
  }
  
  return grouped;
}

/**
 * Obtiene todas las superficies como array.
 */
export function getAllSurfaces(): Array<{ id: string; surface: SurfaceDefinition }> {
  return Object.entries(surfaceIndex).map(([id, surface]) => ({ id, surface }));
}

/**
 * Limpia el índice de superficies en memoria.
 * Útil para recargar el índice. Las texturas horneadas viven en cada
 * `scene.textures` (por instancia), no acá.
 */
export function clearSurfaceCache(): void {
  surfaceIndex = {};
}

/**
 * Carga directamente un índice de superficies (sin cargar de assets-index.json).
 * Útil para el Map Editor cuando se editan superficies.
 */
export function loadSurfaceIndex(surfaces: SurfaceIndex): void {
  surfaceIndex = { ...surfaceIndex, ...surfaces };
}
